import React, { useState, useEffect, useRef, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { MobileTimePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import './componentreg.css';
import { useForm } from 'react-hook-form';
import { CustomDaySelect } from '../Inputfield/inputfield';
import { shiftgetmodule } from '../../Services/app/shiftservice';
import Swal from 'sweetalert2';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Tooltip, Box, Typography } from '@mui/material';
import { customerbasedshift, shiftadd } from '../../Services/app/masterservice';
import dayjs from 'dayjs';
import { cleanCustomerId } from '../../Services/app/operatorservice';
export default function ComponentAdd({ open, handleClose, handleAdd, dialogOpenCount, datasource, setDatasource, customerId }) {
  const customDaySelectRef = useRef();
  const dialogBackgroundColor = dialogOpenCount === 0 ? '#f7f7f7' : '#ededed';
  const [shiftsmodule, setShiftsmodule] = useState([]);

  const componentNameRef = useRef();

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (componentNameRef.current) {
          componentNameRef.current.focus();
        }
      }, 100); // reduced delay for better responsiveness

      return () => clearTimeout(timer); // cleanup on unmount
    }
  }, [open]);

  const { register, handleSubmit, formState: { errors }, trigger, setValue, reset } = useForm({
    shouldFocusError: true,
    mode: 'onBlur'
  });

  const isSurin = cleanCustomerId(customerId) === window._env_.SURIN_CUSTOMER_ID;

  const defaultShiftForm = useMemo(() => ({
    component_name: '',
    component_number: '',
    image_url: '',
    diagram_url: '',
    operation_number: '',
    operation_name: '',
    operation_type: '',
    jobcard: '',
    drawingcode: '',
    cycle_time: dayjs('00:00:00', 'HH:mm:ss'),
    handling_time: dayjs('00:00:00', 'HH:mm:ss'),
    setupTime: dayjs('00:00:00', 'HH:mm:ss'),
    item_code: '',
    process_name: '',
    program_no: '',
    cost_per_part: '',
    // Yantra Intelligence-context fields — feed safer optimization decisions
    raw_material_grade: '',
    tooling_insert_spec: '',
    fixture_dimensions: '',
  }), []);

  const [shiftForm, setShiftForm] = useState(defaultShiftForm);
  const [processGroupOptions, setProcessGroupOptions] = useState([]);
  // Sequence Numbers -> Balloon Sequences (stored as sequences[].balloon_seq[])
  const [sequences, setSequences] = useState([]);

  useEffect(() => {
    if (!open) {
      reset(defaultShiftForm);
      setSequences([]);
    } else {
      setShiftForm(defaultShiftForm);
      // Start with one empty sequence and no balloons (balloons are added on demand).
      setSequences([{
        sequence: '', touch_time: dayjs('00:00:00', 'HH:mm:ss'),
        balloon_seq: [],
      }]);
    }
  }, [open, reset, defaultShiftForm]);

  useEffect(() => {
    getComponentsAdddata();
    if (cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID) {
      fetchProcessGroups();
    }
  }, []);

  const fetchProcessGroups = async () => {
    const key = 'processgroups';
    try {
      const data = await customerbasedshift(customerId, key);
      const allProcessGroups = data[0]?.value || [];
      const mappedGroups = allProcessGroups.map((item) => ({
        value: item.groupName,
        label: item.groupName
      }));
      setProcessGroupOptions(mappedGroups);
    } catch (error) {
      console.error("Error fetching reason groups:", error);
      setProcessGroupOptions([]);
    }
  };

  const getComponentsAdddata = async () => {
    try {
      const response = await shiftgetmodule();
      if (Array.isArray(response)) {
        const formattedOptions = response.map((item, index) => ({
          value: item,
          label: item,
        }));
        setShiftsmodule(formattedOptions);
      } else {
        console.error('Unexpected response format:', response);
        setShiftsmodule([]);
      }
    } catch (error) {
      const fallbackOptions = [
        { value: 'Multiplication Factor', label: 'Multiplication Factor' },
        { value: 'Divide Factor', label: 'Divide Factor' },
      ];
      setShiftsmodule(fallbackOptions);
      console.error('Error fetching shifts:', error);
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setShiftForm({ ...shiftForm, [name]: value });
    setValue(name, value);
    trigger(name);
  };

  const handleTimeChange = (name, value) => {
    setShiftForm((prevShiftForm) => ({
      ...prevShiftForm,
      [name]: value,
    }));
    setValue(name, value);
    trigger(name);
  };

  // ----- Sequence Numbers / Balloon Sequences -----
  const seqLabelSx = { color: 'black', '&.Mui-focused': { color: 'orange' } };
  const seqFieldSx = {
    background: dialogBackgroundColor,
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: 'black' },
      '&:hover fieldset': { borderColor: 'black' },
      '&.Mui-focused fieldset': { borderColor: 'orange' },
    },
  };
  const blankBalloon = () => ({ sequence: '', touch_time: dayjs('00:00:00', 'HH:mm:ss') });
  const blankSequence = () => ({ sequence: '', touch_time: dayjs('00:00:00', 'HH:mm:ss'), balloon_seq: [] });

  const addSequence = () => setSequences((p) => [...p, blankSequence()]);
  const removeSequence = (i) => setSequences((p) => p.filter((_, idx) => idx !== i));
  const updateSequence = (i, key, value) =>
    setSequences((p) => p.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)));
  const addBalloon = (i) =>
    setSequences((p) => p.map((s, idx) => (idx === i ? { ...s, balloon_seq: [...s.balloon_seq, blankBalloon()] } : s)));
  const removeBalloon = (i, j) =>
    setSequences((p) => p.map((s, idx) => (idx === i ? { ...s, balloon_seq: s.balloon_seq.filter((_, bIdx) => bIdx !== j) } : s)));
  const updateBalloon = (i, j, key, value) =>
    setSequences((p) =>
      p.map((s, idx) =>
        idx === i ? { ...s, balloon_seq: s.balloon_seq.map((b, bIdx) => (bIdx === j ? { ...b, [key]: value } : b)) } : s
      )
    );

  // Convert the sequences UI state into the stored JSON shape.
  const buildSequencesPayload = () =>
    sequences
      .filter((s) => String(s.sequence).trim() !== '')
      .map((s) => ({
        sequence: String(s.sequence).trim(),
        touch_time: dayjs(s.touch_time).isValid() ? dayjs(s.touch_time).format('HH:mm:ss') : '00:00:00',
        balloon_seq: (s.balloon_seq || [])
          .filter((b) => String(b.sequence).trim() !== '')
          .map((b) => ({
            sequence: String(b.sequence).trim(),
            touch_time: dayjs(b.touch_time).isValid() ? dayjs(b.touch_time).format('HH:mm:ss') : '00:00:00',
          })),
      }));

  const onSubmit = async (data) => {
    try {
      const timeFields = isSurin
        ? [{ key: "cycle_time", label: "Machining Time" }]
        : [
            { key: "cycle_time", label: "Machining Time" },
            { key: "handling_time", label: "Handling Time" },
            { key: "setupTime", label: "Setup Time" },
          ];
      for (const { key, label } of timeFields) {
        const value = data[key];
        if (!value || !dayjs(value).isValid()) {
          handleClose();
          Swal.fire({
            icon: "error",
            title: "Invalid Input",
            text: `${label} has an invalid or incomplete time format.`,
          });
          return;
        }
        if (key === "cycle_time" && dayjs(value).format("HH:mm:ss") === "00:00:00") {
          handleClose();
          Swal.fire({
            icon: "error",
            title: "Invalid Input",
            text: "Machining Time must not be 00:00:00.",
          });
          return;
        }
      }

      // Validate sequences for SURIN
      if (isSurin) {
        for (let i = 0; i < sequences.length; i++) {
          if (!sequences[i].sequence || sequences[i].sequence.trim() === '') {
            handleClose();
            Swal.fire({
              icon: "error",
              title: "Invalid Input",
              text: `Sequence ${i + 1} is required.`,
            });
            return;
          }
          if (!sequences[i].touch_time || !dayjs(sequences[i].touch_time).isValid()) {
            handleClose();
            Swal.fire({
              icon: "error",
              title: "Invalid Input",
              text: `Touch Time for Sequence ${i + 1} has an invalid format.`,
            });
            return;
          }
        }
      }

      const safeFormat = (v) => dayjs(v).format("HH:mm:ss");
      const id = Math.random().toString(36).substr(2, 9);
      const isGplast = cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID;

      const currentShiftData = {
        id,
        component_name: data.component_name,
        component_number: data.component_number,
        operation_number: data.operation_number,
        operation_name: data.operation_name,
        operation_type: data.operation_type,
        factorval: data.factorval,
        jobcard: data.jobcard,
        drawingcode: data.drawingcode,
        factor: data.factor,
        cycle_time: safeFormat(data.cycle_time),
        handling_time: safeFormat(data.handling_time),
        setupTime: safeFormat(data.setupTime),
        sequences: buildSequencesPayload(),
        ...(cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID && {
          item_code: data.item_code,
          process_name: data.process_name,
        }),
      };

      let existingShifts = Array.isArray(datasource) ? [...datasource] : [];
      const normalize = (val) => (val || "").trim().toLowerCase();

      if (isSurin) {
        const duplicate = existingShifts.find(
          (shift) =>
            normalize(shift.component_name) === normalize(data.component_name)
        );
        if (duplicate) {
          handleClose();
          Swal.fire(
            "Duplicate entry",
            "Component with same name already exists",
            "error"
          );
          return;
        }
      } else if (isGplast) {
        const duplicate = existingShifts.find((shift) => {
          return (
            normalize(shift.component_name) === normalize(data.component_name) &&
            normalize(shift.item_code) === normalize(data.item_code) &&
            normalize(shift.process_name) === normalize(data.process_name) &&
            normalize(shift.operation_number) === normalize(data.operation_number)
          );
        });

        if (duplicate) {
          handleClose();
          Swal.fire(
            "Duplicate entry",
            "Component with same Item Code and Process Name already exists",
            "error"
          );
          return;
        }
      } else {
        const duplicateNumber = existingShifts.find(
          (shift) =>
            normalize(shift.component_number) === normalize(data.component_number)
        );
        if (duplicateNumber) {
          handleClose();
          Swal.fire(
            "Duplicate entry",
            "Component Number already exists",
            "error"
          );
          return;
        }
      }

      existingShifts.push(currentShiftData);

      const formData = {
        component: existingShifts,
        lastUpdateTs: Date.now(),
      };

      console.log("Submitted component data:", JSON.stringify(formData));
      const scope = "SERVER_SCOPE";
      const response = await shiftadd(formData, customerId, scope);

      Swal.fire(response.msg || "Component created successfully!", "", "success");

      handleClose();
      reset(defaultShiftForm);
    } catch (error) {
      console.error("Error submitting data:", error);
      handleClose();
      reset(defaultShiftForm);
      Swal.fire("Error", `Error submitting data: ${error.message}`, "error");
    }
  };


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="300px" PaperProps={{ style: { backgroundColor: dialogBackgroundColor } }}>
      <DialogTitle style={{ color: 'black' }}>{isSurin ? 'Component Registration' : 'Add Component'}</DialogTitle>
      <div className="close_modal">
        <Tooltip title="Close">
          <IconButton aria-label="close" onClick={handleClose} style={{ backgroundColor: 'var(--y-bg, #ffffff)' }}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className="machinedialog">
        <div className="filter_sec">
          <form onSubmit={handleSubmit(onSubmit)} className="form_sec shift_form component_form_even" autoComplete="off">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="form_sec_fields">
                {/* Component Name Field */}
                <div className={`form_field ${errors.component_name ? 'error-outline' : ''}`}>
                  <TextField
                    inputRef={componentNameRef}
                    {...register("component_name", {
                      required: "Component Name is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      }
                    })}
                    label={isSurin ? "Comp. Description" : "Component Name"}
                    type="text"
                    name="component_name"
                    value={shiftForm.component_name}
                    onChange={handleFormChange}
                    error={!!errors.component_name}
                    InputLabelProps={{
                      required: true,
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    inputProps={{ maxLength: 100 }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.component_name && <div className="mat-error">{errors.component_name.message}</div>}
                </div>
                {(cleanCustomerId(customerId) != window._env_.GPLAST_CUSTOMER_ID && !isSurin) && (
                  <div className={`form_field ${errors.component_number ? 'error-outline' : ''}`}>
                    <TextField
                      {...register("component_number", {
                        required: "Component Number is required",
                        maxLength: {
                          value: 100,
                          message: "Maximum length is 100 characters"
                        }
                      })}
                      label="Component Number"
                      type="text"
                      name="component_number"
                      value={shiftForm.component_number}
                      onChange={handleFormChange}
                      error={!!errors.component_number}
                      InputLabelProps={{
                        required: true,
                        sx: {
                          color: 'black',
                          '&.Mui-focused': {
                            color: 'orange',
                          },
                        },
                      }}
                      inputProps={{ maxLength: 100 }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'black',
                          },
                          '&:hover fieldset': {
                            borderColor: 'black',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'orange',
                          },
                          '& .MuiOutlinedInput-input': {
                            color: 'black',
                          },
                          '&.Mui-focused .MuiOutlinedInput-input': {
                            caretColor: 'orange',
                          },
                          '&::placeholder': {
                            color: 'black',
                            opacity: 1,
                          },
                        },
                      }}
                    />
                    {errors.component_number && (
                      <div className="mat-error">{errors.component_number.message}</div>
                    )}
                  </div>
                )}
                {(cleanCustomerId(customerId) === window._env_.ATECH_CUSTOMER_ID || cleanCustomerId(customerId) === window._env_.HITECH_CUSTOMER_ID) && (
                  <div className={`form_field ${errors.operation_type ? 'error-outline' : ''}`}>
                    <CustomDaySelect
                      {...register("operation_type", { required: "Operation type is required" })}
                      onBlur={() => trigger('operation_type')}
                      name="operation_type"
                      value={shiftForm.operation_type}
                      onChange={handleFormChange}
                      label="Operation Type"
                      required={true}
                      options={[
                        { value: 'VMC', label: 'VMC' },
                        { value: 'HMC', label: 'HMC' },
                        { value: 'CNC', label: 'CNC' },
                        { value: 'TC', label: 'TC' },
                      ]}
                      error={!!errors.operation_type}
                      sx={{
                        '& .MuiInputLabel-root': {
                          top: '-6px',
                          backgroundColor: '#ededed',
                          padding: '0 4px',
                        },
                      }}
                    />
                    {errors.operation_type && <div className="mat-error">{errors.operation_type.message}</div>}
                  </div>
                )}


                {/* Operation Number Field 
                <div className={`form_field ${errors.operation_number ? 'error-outline' : ''}`}>
                  <TextField
                   {...register("operation_number", {
                    required: "Mould Number is required",
                    maxLength: {
                      value: 100,
                      message: "Maximum length is 100 characters"
                    },
                    setValueAs: v => v === "" ? "" : Number(v), // Converts to number or empty
                    validate: value => {
                      if (isNaN(value)) return "Mould Number must be a number";
                      if (value <= 0) return "Mould Number must be greater than 0";
                      return true;
                    }
                  })}
                  
                    label="Mould Number"
                    type="number"
                    name="operation_number"
                    value={shiftForm.operation_number}
                    onChange={handleFormChange}
                    error={!!errors.operation_number}
                    inputProps={{ maxLength: 100, min: 1 }}
                    InputLabelProps={{
                      required: true, 
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.operation_number && <div className="mat-error">{errors.operation_number.message}</div>}
                </div>  */}

                {/* Operation Name Field 
             
                 <div className={`form_field  ${errors.operation_name ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("operation_name", { required: "Mould Name is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      validate: value =>
                        /^[a-zA-Z0-9\s]*$/.test(value) || "Special characters are not allowed"
                    })}
                    onBlur={() => trigger('operation_name')}
                    label="Mould Name"
                    type="text"
                    name="operation_name"
                    value={shiftForm.operation_name}
                    onChange={handleFormChange}
                    error={!!errors.operation_name}
                    inputProps={{ maxLength: 100 }}
                    InputLabelProps={{
                      required: true, 
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.operation_name && (
  <div className="mat-error">{errors.operation_name.message}</div>
)}
                </div>  */}



                {cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID && (
                  <>
                    <div className={`form_field ${errors.item_code ? 'error-outline' : ''}`}>
                      <TextField
                        {...register("item_code", {
                          required: "Item Code is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum length is 100 characters"
                          },
                        })}
                        label="Item Code"
                        type="text"
                        name="item_code"
                        value={shiftForm.item_code || ""}
                        onChange={handleFormChange}
                        error={!!errors.item_code}
                        InputLabelProps={{
                          required: true,
                          sx: {
                            color: "black",
                            "&.Mui-focused": {
                              color: "orange",
                            },
                          },
                        }}
                        inputProps={{ maxLength: 100 }}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "black",
                            },
                            "&:hover fieldset": {
                              borderColor: "black",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "orange",
                            },
                            "& .MuiOutlinedInput-input": {
                              color: "black",
                            },
                            "&.Mui-focused .MuiOutlinedInput-input": {
                              caretColor: "orange",
                            },
                            "&::placeholder": {
                              color: "black",
                              opacity: 1,
                            },
                          },
                        }}
                      />
                      {errors.item_code && (
                        <div className="mat-error">{errors.item_code.message}</div>
                      )}
                    </div>
                    <div className={`form_field ${errors.process_name ? "error-outline" : ""}`}>
                      <CustomDaySelect
                        {...register("process_name", { required: "Process Name is required" })}
                        onBlur={() => trigger("process_name")}
                        name="process_name"
                        value={shiftForm.process_name || ""}
                        onChange={handleFormChange}
                        label="Process Name"
                        required={true}
                        options={processGroupOptions}
                        error={!!errors.process_name}
                      />
                      {errors.process_name && (
                        <div className="mat-error">{errors.process_name.message}</div>
                      )}
                    </div>
                    <div className={`form_field ${errors.operation_number ? 'error-outline' : ''}`}>
                      <TextField
                        {...register("operation_number", {
                          required: "Operation Number is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum length is 100 characters"
                          },
                        })}
                        label="Operation Number"
                        type="text"
                        name="operation_number"
                        value={shiftForm.operation_number || ""}
                        onChange={handleFormChange}
                        error={!!errors.operation_number}
                        InputLabelProps={{
                          required: true,
                          sx: {
                            color: "black",
                            "&.Mui-focused": {
                              color: "orange",
                            },
                          },
                        }}
                        inputProps={{ maxLength: 100 }}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "black",
                            },
                            "&:hover fieldset": {
                              borderColor: "black",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "orange",
                            },
                            "& .MuiOutlinedInput-input": {
                              color: "black",
                            },
                            "&.Mui-focused .MuiOutlinedInput-input": {
                              caretColor: "orange",
                            },
                            "&::placeholder": {
                              color: "black",
                              opacity: 1,
                            },
                          },
                        }}
                      />
                      {errors.operation_number && (
                        <div className="mat-error">{errors.operation_number.message}</div>
                      )}
                    </div>
                  </>
                )}

                {/* ===== SURIN-specific fields ===== */}
                {isSurin && (
                  <>
                    <div className={`form_field ${errors.component_number ? 'error-outline' : ''}`}>
                      <TextField
                        {...register("component_number", {
                          required: "Comp. Drawing No is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum length is 100 characters"
                          },
                        })}
                        label="Comp. Drawing No"
                        type="text"
                        name="component_number"
                        value={shiftForm.component_number}
                        onChange={handleFormChange}
                        error={!!errors.component_number}
                        InputLabelProps={{
                          required: true,
                          sx: {
                            color: 'black',
                            '&.Mui-focused': { color: 'orange' },
                          },
                        }}
                        inputProps={{ maxLength: 100 }}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'black' },
                            '&:hover fieldset': { borderColor: 'black' },
                            '&.Mui-focused fieldset': { borderColor: 'orange' },
                            '& .MuiOutlinedInput-input': { color: 'black' },
                            '&.Mui-focused .MuiOutlinedInput-input': { caretColor: 'orange' },
                            '&::placeholder': { color: 'black', opacity: 1 },
                          },
                        }}
                      />
                      {errors.component_number && <div className="mat-error">{errors.component_number.message}</div>}
                    </div>
                  </>
                )}

                {/* Factor Name + Factor Value fields removed per request */}
                {/* ===== Component Image + Diagram upload (hidden for GPLAST) ===== */}
                {(cleanCustomerId(customerId) != window._env_.GPLAST_CUSTOMER_ID) && (
                  <div className="form_field" style={{ width: '100%', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 320px', display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 8, background: 'var(--y-bg-soft, #fafafa)' }}>
                      {shiftForm.image_url ? (
                        <img src={`${(window._env_?.SERVER_URL2 || '').replace(/\/$/, '')}${shiftForm.image_url}`}
                             alt="component"
                             style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--y-border, #d1d5db)' }} />
                      ) : (
                        <div style={{ width: 64, height: 64, background: 'var(--y-bg, #f3f4f6)', border: '1px dashed #d1d5db', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 28 }}>+</div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 2 }}>Component Image</label>
                        <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6 }}>JPG / PNG / WEBP, max 10 MB</div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          style={{ fontSize: 12, width: '100%' }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 10 * 1024 * 1024) { Swal.fire({ icon: 'error', title: 'Image too large', text: 'Max 10 MB allowed.' }); return; }
                            const code = shiftForm.component_number || '';
                            if (!code) { Swal.fire({ icon: 'warning', title: 'Enter Component Number first' }); return; }
                            try {
                              const base = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');
                              const fd = new FormData();
                              fd.append('customer_id', cleanCustomerId(customerId));
                              fd.append('component_code', code);
                              fd.append('image', file);
                              const res = await fetch(`${base}/api/operator-assignment/component-image`, { method: 'POST', body: fd });
                              const j = await res.json();
                              if (j.url) { setShiftForm((prev) => ({ ...prev, image_url: j.url })); setValue('image_url', j.url); }
                              else Swal.fire({ icon: 'error', title: 'Upload failed', text: j.message || 'Try again.' });
                            } catch (err) { Swal.fire({ icon: 'error', title: 'Upload error', text: err.message }); }
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ flex: '1 1 320px', display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 8, background: 'var(--y-bg-soft, #fafafa)' }}>
                      {shiftForm.diagram_url ? (
                        shiftForm.diagram_url.toLowerCase().endsWith('.pdf') ? (
                          <a href={`${(window._env_?.SERVER_URL2 || '').replace(/\/$/, '')}${shiftForm.diagram_url}`}
                             target="_blank" rel="noreferrer"
                             style={{ width: 64, height: 64, background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                             title="Open PDF">
                            <span style={{ fontSize: 12, fontWeight: 800, color: '#b45309' }}>PDF</span>
                          </a>
                        ) : (
                          <img src={`${(window._env_?.SERVER_URL2 || '').replace(/\/$/, '')}${shiftForm.diagram_url}`}
                               alt="diagram"
                               style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--y-border, #d1d5db)' }} />
                        )
                      ) : (
                        <div style={{ width: 64, height: 64, background: 'var(--y-bg, #f3f4f6)', border: '1px dashed #d1d5db', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 11 }}>DIAG</div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 2 }}>Component Diagram</label>
                        <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6 }}>JPG / PNG / WEBP / PDF, max 10 MB</div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,application/pdf"
                          style={{ fontSize: 12, width: '100%' }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 10 * 1024 * 1024) { Swal.fire({ icon: 'error', title: 'Diagram too large', text: 'Max 10 MB allowed.' }); return; }
                            const code = shiftForm.component_number || '';
                            if (!code) { Swal.fire({ icon: 'warning', title: 'Enter Component Number first' }); return; }
                            try {
                              const base = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');
                              const fd = new FormData();
                              fd.append('customer_id', cleanCustomerId(customerId));
                              fd.append('component_code', code);
                              fd.append('diagram', file);
                              const res = await fetch(`${base}/api/operator-assignment/component-diagram`, { method: 'POST', body: fd });
                              const j = await res.json();
                              if (j.url) { setShiftForm((prev) => ({ ...prev, diagram_url: j.url })); setValue('diagram_url', j.url); }
                              else Swal.fire({ icon: 'error', title: 'Upload failed', text: j.message || 'Try again.' });
                            } catch (err) { Swal.fire({ icon: 'error', title: 'Upload error', text: err.message }); }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ===== Machining Time ===== */}
                <div className={`form_field ${errors.cycle_time ? "error-outline" : ""}`}>
                  <DemoItem className="white-label">
                    <TimePicker
                      {...register("cycle_time", {
                        required: "Machining Time is required",
                        validate: (value) => {
                          if (!value) return "Machining Time is required";
                          if (!dayjs(value).isValid()) return "Invalid time format";
                          return true;
                        },
                      })}
                      onBlur={() => trigger("cycle_time")}
                      value={shiftForm.cycle_time}
                      onChange={(value) => handleTimeChange("cycle_time", value)}
                      views={["hours", "minutes", "seconds"]}
                      format="HH:mm:ss"
                      ampm={false}
                      label="Machining Time *"
                      error={!!errors.cycle_time}
                      slotProps={{
                        textField: {
                          helperText: errors.cycle_time?.message,
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: errors.cycle_time ? "red" : "black",
                          },
                          "&:hover fieldset": { borderColor: "black" },
                          "&.Mui-focused fieldset": {
                            borderColor: errors.cycle_time ? "red" : "orange",
                          },
                          "& .MuiOutlinedInput-input": { color: "black" },
                          "&.Mui-focused .MuiOutlinedInput-input": { caretColor: "orange" },
                        },
                      }}
                    />
                  </DemoItem>
                </div>

                {/* ===== Handling Time ===== */}
                <div className={`form_field ${errors.handling_time ? "error-outline" : ""}`}>
                  <DemoItem>
                    <TimePicker
                      {...register("handling_time", {
                        required: isSurin ? false : "Handling Time is required",
                        validate: (value) => {
                          if (isSurin && (!value || dayjs(value).format("HH:mm:ss") === "00:00:00")) return true;
                          if (!value) return "Handling Time is required";
                          if (!dayjs(value).isValid()) return "Invalid time format";
                          return true;
                        },
                      })}
                      onBlur={() => trigger("handling_time")}
                      value={shiftForm.handling_time}
                      onChange={(value) => handleTimeChange("handling_time", value)}
                      views={["hours", "minutes", "seconds"]}
                      format="HH:mm:ss"
                      ampm={false}
                      label={isSurin ? "Handling Time" : "Handling Time *"}
                      error={!!errors.handling_time}
                      slotProps={{
                        textField: {
                          helperText: errors.handling_time?.message,
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: errors.handling_time ? "red" : "black",
                          },
                          "&:hover fieldset": { borderColor: "black" },
                          "&.Mui-focused fieldset": {
                            borderColor: errors.handling_time ? "red" : "orange",
                          },
                          "& .MuiOutlinedInput-input": { color: "black" },
                          "&.Mui-focused .MuiOutlinedInput-input": { caretColor: "orange" },
                        },
                      }}
                    />
                  </DemoItem>
                </div>

                {/* ===== Setup Time (hidden for SURIN) ===== */}
                {!isSurin && (
                  <div className={`form_field ${errors.setupTime ? "error-outline" : ""}`}>
                    <DemoItem>
                      <TimePicker
                        {...register("setupTime", {
                          required: "Setup Time is required",
                          validate: (value) => {
                            if (!value) return "Setup Time is required";
                            if (!dayjs(value).isValid()) return "Invalid time format";
                            return true;
                          },
                        })}
                        onBlur={() => trigger("setupTime")}
                        value={shiftForm.setupTime}
                        onChange={(value) => handleTimeChange("setupTime", value)}
                        views={["hours", "minutes", "seconds"]}
                        format="HH:mm:ss"
                        ampm={false}
                        label="Setup Time *"
                        error={!!errors.setupTime}
                        slotProps={{
                          textField: {
                            helperText: errors.setupTime?.message,
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: errors.setupTime ? "red" : "black",
                            },
                            "&:hover fieldset": { borderColor: "black" },
                            "&.Mui-focused fieldset": {
                              borderColor: errors.setupTime ? "red" : "orange",
                            },
                            "& .MuiOutlinedInput-input": { color: "black" },
                            "&.Mui-focused .MuiOutlinedInput-input": { caretColor: "orange" },
                          },
                        }}
                      />
                    </DemoItem>
                  </div>
                )}

                {/* ===== Program Number & Cost per part ===== */}
                <div className={`form_field`}>
                  <TextField
                    {...register("program_no", {
                      maxLength: { value: 60, message: "Maximum length is 60 characters" },
                    })}
                    label="Program Number"
                    type="text"
                    name="program_no"
                    value={shiftForm.program_no || ''}
                    onChange={handleFormChange}
                    inputProps={{ maxLength: 60 }}
                    InputLabelProps={{ sx: { color: 'black', '&.Mui-focused': { color: 'orange' } } }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'black' },
                        '&:hover fieldset': { borderColor: 'black' },
                        '&.Mui-focused fieldset': { borderColor: 'orange' },
                        '& .MuiOutlinedInput-input': { color: 'black' },
                      },
                    }}
                  />
                  {errors.program_no && <div className="mat-error">{errors.program_no.message}</div>}
                </div>

                <div className={`form_field ${errors.cost_per_part ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("cost_per_part", {
                      validate: (v) => v === '' || v == null || (!isNaN(Number(v)) && Number(v) >= 0) || "Cost must be a non-negative number",
                    })}
                    label="Cost per part (₹)"
                    type="number"
                    name="cost_per_part"
                    value={shiftForm.cost_per_part ?? ''}
                    onChange={handleFormChange}
                    inputProps={{ step: '0.01', min: 0 }}
                    InputLabelProps={{ sx: { color: 'black', '&.Mui-focused': { color: 'orange' } } }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'black' },
                        '&:hover fieldset': { borderColor: 'black' },
                        '&.Mui-focused fieldset': { borderColor: 'orange' },
                        '& .MuiOutlinedInput-input': { color: 'black' },
                      },
                    }}
                  />
                  {errors.cost_per_part && <div className="mat-error">{errors.cost_per_part.message}</div>}
                </div>

                {/* ===== Yantra Intelligence Context — material, tooling, fixture ===== */}
                <div style={{
                    width: '100%', marginTop: 10, marginBottom: 10,
                    border: '1px solid #c4b5fd', background: '#faf5ff', borderRadius: 8, padding: 12,
                }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#5b21b6', marginBottom: 10, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                        🤖 Yantra Intelligence Context (safer optimizations)
                    </div>
                    <div className="form_field" style={{ marginBottom: 10 }}>
                        <TextField
                            label="Raw Material Grade"
                            name="raw_material_grade"
                            value={shiftForm.raw_material_grade || ''}
                            onChange={handleFormChange}
                            placeholder='e.g. "AL 6061-T6", "SS 304", "EN8 carbon steel"'
                            helperText="Drives safe feed/speed range"
                            InputLabelProps={{ sx: { color: 'black', '&.Mui-focused': { color: 'orange' } } }}
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'black' }, '&:hover fieldset': { borderColor: 'black' }, '&.Mui-focused fieldset': { borderColor: 'orange' }, '& .MuiOutlinedInput-input': { color: 'black' } } }}
                        />
                    </div>
                    <div className="form_field" style={{ marginBottom: 10 }}>
                        <TextField
                            label="Tooling / Insert Spec"
                            name="tooling_insert_spec"
                            value={shiftForm.tooling_insert_spec || ''}
                            onChange={handleFormChange}
                            placeholder='e.g. "CCMT09T308 carbide PVD, AlTiN coated"'
                            helperText="Constrains chip load + cutting speed"
                            InputLabelProps={{ sx: { color: 'black', '&.Mui-focused': { color: 'orange' } } }}
                            fullWidth multiline maxRows={2}
                            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'black' }, '&:hover fieldset': { borderColor: 'black' }, '&.Mui-focused fieldset': { borderColor: 'orange' }, '& .MuiOutlinedInput-input': { color: 'black' } } }}
                        />
                    </div>
                    <div className="form_field">
                        <TextField
                            label="Fixture Dimensions"
                            name="fixture_dimensions"
                            value={shiftForm.fixture_dimensions || ''}
                            onChange={handleFormChange}
                            placeholder='e.g. "200×150×80mm, 4-jaw chuck, 25mm jaw height"'
                            helperText="Tells Yantra Intelligence safe Z-clearance height (prevents stripping safety Z-rapids)"
                            InputLabelProps={{ sx: { color: 'black', '&.Mui-focused': { color: 'orange' } } }}
                            fullWidth multiline maxRows={2}
                            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'black' }, '&:hover fieldset': { borderColor: 'black' }, '&.Mui-focused fieldset': { borderColor: 'orange' }, '& .MuiOutlinedInput-input': { color: 'black' } } }}
                        />
                    </div>
                </div>

                {/* Operation Number Field
                <div className={`form_field ${errors.jobcard ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("jobcard", { required: "Job Card is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      validate: value =>
                        /^[a-zA-Z0-9\s]*$/.test(value) || "Special characters are not allowed" })}
                    label="Raw Material"
                    type="text"
                    name="jobcard"
                    value={shiftForm.jobcard}
                    onChange={handleFormChange}
                    error={!!errors.jobcard}
                    inputProps={{ maxLength: 100}}
                    InputLabelProps={{
                      required: true, 
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.jobcard && <div className="mat-error">{errors.jobcard.message}</div>}
                </div>
                <div className={`form_field ${errors.drawingcode ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("drawingcode", { required: "Drawingcode is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      validate: value => {
                        if (isNaN(value)) return "Drawing code  must be a number";
                        if (Number(value) <= 0) return "Drawing code must be greater than 0";
                        return true;
                      }
                     })}
                    label="Raw Material Code"
                    type="number"
                    name="drawingcode"
                    value={shiftForm.drawingcode}
                    onChange={handleFormChange}
                    error={!!errors.drawingcode}
                    inputProps={{ maxLength: 100, min: 1 }}
                    InputLabelProps={{
                      required: true, 
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.drawingcode && <div className="mat-error">{errors.drawingcode.message}</div>}
                </div>  */}

                {/* ===== Sequence Numbers / Balloon Sequences (Surin customer only) ===== */}
                {cleanCustomerId(customerId) === window._env_.SURIN_CUSTOMER_ID && (
                <Box sx={{ gridColumn: '1 / -1', flexBasis: '100%', width: '100%', mt: 1, boxSizing: 'border-box' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#000', mb: 1.5 }}>Sequence Numbers</Typography>

                  {sequences.map((seq, i) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                          label="Sequence"
                          required
                          value={seq.sequence}
                          onChange={(e) => updateSequence(i, 'sequence', e.target.value)}
                          InputLabelProps={{ required: true, sx: seqLabelSx }}
                          sx={{ flex: 1, minWidth: 220, ...seqFieldSx }}
                        />
                        <TimePicker
                          label="Touch Time *"
                          value={seq.touch_time}
                          onChange={(v) => updateSequence(i, 'touch_time', v)}
                          views={['hours', 'minutes', 'seconds']}
                          format="HH:mm:ss"
                          ampm={false}
                          slotProps={{ textField: { sx: { flex: 1, minWidth: 220, ...seqFieldSx } } }}
                        />
                        {i > 0 && (
                          <Tooltip title="Remove sequence">
                            <IconButton onClick={() => removeSequence(i)} sx={{ color: '#b00020' }}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      {/* Balloon Sequences — only shown once a balloon is added */}
                      {seq.balloon_seq.length > 0 && (
                        <Box sx={{ borderLeft: '3px solid #ec6e17', pl: 2, ml: 1, mt: 1.5 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#000', mb: 1.5 }}>Balloon Sequences</Typography>
                          {seq.balloon_seq.map((b, j) => (
                            <Box key={j} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                              <TextField
                                label="Sequence"
                                required
                                value={b.sequence}
                                onChange={(e) => updateBalloon(i, j, 'sequence', e.target.value)}
                                InputLabelProps={{ required: true, sx: seqLabelSx }}
                                sx={{ flex: 1, minWidth: 200, ...seqFieldSx }}
                              />
                              <TimePicker
                                label="Touch Time *"
                                value={b.touch_time}
                                onChange={(v) => updateBalloon(i, j, 'touch_time', v)}
                                views={['hours', 'minutes', 'seconds']}
                                format="HH:mm:ss"
                                ampm={false}
                                slotProps={{ textField: { sx: { flex: 1, minWidth: 200, ...seqFieldSx } } }}
                              />
                              <Tooltip title="Remove balloon sequence">
                                <IconButton onClick={() => removeBalloon(i, j)} sx={{ color: '#b00020' }}>
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Two actions under each sequence */}
                      <Box sx={{ display: 'flex', gap: 2, mt: 1, ml: 1, flexWrap: 'wrap' }}>
                        <Button
                          onClick={() => addBalloon(i)}
                          startIcon={<AddCircleOutlineIcon />}
                          size="small"
                          sx={{ textTransform: 'none', color: '#ec6e17', fontWeight: 600 }}
                        >
                          Add Balloon
                        </Button>
                        {i === sequences.length - 1 && (
                          <Button
                            onClick={addSequence}
                            startIcon={<AddCircleOutlineIcon />}
                            size="small"
                            sx={{ textTransform: 'none', color: '#ec6e17', fontWeight: 600 }}
                          >
                            Add Sequence
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
                )}

              </div>
            </LocalizationProvider>
            <div className="form-button text-right" align="end" style={{ marginRight: '10px' }}>
              <Button type="submit" variant="contained" className="filter_btn btn_orange" color="warning" >
                Save
              </Button>
            </div>
            <br></br>
          </form>
        </div>
      </div>
    </Dialog>
  );
}